import React from 'react';
import { useTranslation } from 'react-i18next';
import type { AccountTransaction } from '@hero/schema';
import { formatDateMain } from '@hero/core';
import { cn } from '@hero/web-lib';
import { useAccountTransactionDrawerStore } from '@hero/hooks-shared';
import { CalendarIcon, PayIcon, TransactionIcon } from '@hero/web-icons';
import { Card } from '../../../app/Card/Card';
import { AccountTransactionStatusBadge } from '../Status/AccountTransactionStatusBadge';
import { PriceBadge } from '../../../app/Price/PriceBadge';
import { RefDisplay } from '../../../app';

export interface AccountTransactionCardProps {
  transaction: AccountTransaction;
  className?: string;
}

/**
 * List card for an `AccountTransaction`. Mirrors `TransactionCard`
 * but reads the bilateral schema:
 *
 * - `transaction.amount` is signed from `parties[0]`'s POV; the
 *   sign-only colouring is driven by `flow` so each viewer sees
 *   the right "+/-" without us needing to know which side of the
 *   account the viewer is on (the parent list scopes the data).
 * - The order ref (when present on a leg-tied tx) is surfaced as
 *   the primary identifier; the tx ref itself is the fallback.
 */
export const AccountTransactionCard: React.FC<AccountTransactionCardProps> = ({
  transaction,
  className,
}) => {
  const { t } = useTranslation(['m-account-transaction']);
  const openDrawer = useAccountTransactionDrawerStore(s => s.open);

  const isFailed = transaction.status === 'failed';
  const isCredit = transaction.flow === 1;

  const typeLabel = t(`type.${transaction.type}`, { defaultValue: transaction.type });
  const dateStr = transaction.createdAt
    ? formatDateMain({ format: 'date_day', date: transaction.createdAt })
    : undefined;

  const priceColor = isFailed ? 'gray' : isCredit ? 'green' : 'red';
  const pricePrefix = isFailed ? undefined : isCredit ? '+' : undefined;

  return (
    <Card onClick={() => openDrawer(String(transaction._id))} className={className}>
      {/* Row 1: type + status */}
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-sm font-medium text-gray-700 min-w-0">
          <div className="flex items-center justify-center rounded-md bg-gray-50 p-1.5 flex-shrink-0">
            <TransactionIcon className="size-3.5 text-gray-400" />
          </div>
          <span className="truncate">{typeLabel}</span>
        </div>
        <AccountTransactionStatusBadge status={transaction.status} />
      </div>

      {/* Row 2: tx ref / order ref / date / gateway */}
      <div className="flex flex-wrap items-center justify-evenly gap-y-1 mt-2">
        <RefDisplay
          refMain={transaction.order?.ref?.main ?? transaction.ref?.main}
          refShort={transaction.order?.ref?.short ?? transaction.ref?.short}
        />

        {dateStr && (
          <>
            <span className="text-gray-300">·</span>
            <div className="flex items-center gap-1.5 text-xs text-gray-500 flex-shrink-0">
              <CalendarIcon className="size-3 text-gray-400" />
              <span>{dateStr}</span>
            </div>
          </>
        )}

        {transaction.gateway?.method && (
          <>
            <span className="text-gray-300">·</span>
            <div className="flex items-center gap-1.5 text-xs text-gray-500">
              <PayIcon className="size-3 text-gray-400" />
              <span>
                {t(`m-payment:payment_methods.${transaction.gateway.method}`, {
                  defaultValue: transaction.gateway.method,
                })}
              </span>
            </div>
          </>
        )}
      </div>

      {/* Row 3: amount */}
      <div className="flex items-center justify-end mt-2">
        <PriceBadge
          price={Math.abs(transaction.amount)}
          currency={transaction.currency}
          color={priceColor}
          prefix={pricePrefix}
          className={cn('ms-auto')}
        />
      </div>
    </Card>
  );
};
